import { captureException, showReportDialog, withScope } from '@sentry/browser'
import {
  Text as BaseText,
  Box,
  Flex,
  Heading,
  PrimaryButton,
  SideBySideLayout,
} from 'mds-web-ui'
import { ActiveLink } from 'mds-web-ui/components/Link/ReactRouterLink'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { addTags } from './Sentry'

const CHUNK_ERROR_RELOAD_KEY = 'chunkErrorReloaded'

const CHUNK_LOAD_ERROR_PATTERNS = [
  'ChunkLoadError',
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'is not a valid JavaScript MIME type',
]

const isChunkLoadError = (error) => {
  if (!error) return false

  const name = error.name || ''
  const message = error.message || ''

  if (name.includes('ChunkLoadError')) return true

  return CHUNK_LOAD_ERROR_PATTERNS.some(
    (pattern) => pattern !== 'ChunkLoadError' && message.includes(pattern),
  )
}

const recoverFromChunkLoadError = async () => {
  if (sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY) === 'true') {
    console.warn('Chunk load error persists after reload, not retrying.')
    return false
  }

  console.warn('Chunk load error detected, clearing cache and reloading...')
  sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, 'true')

  try {
    const cacheNames = await caches.keys()

    if (cacheNames.length > 0) {
      console.log(`Clearing ${cacheNames.length} cache(s)...`)
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
    }
  } catch (err) {
    console.error('Failed to clear cache:', err)
  }

  window.location.reload()
  return true
}

const handleChunkLoadError = async (error) => {
  if (!isChunkLoadError(error)) {
    return
  }

  await recoverFromChunkLoadError()
}

if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    try {
      sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY)
    } catch {}
  })

  window.addEventListener('vite:preloadError', (event) => {
    if (!isChunkLoadError(event.payload)) {
      return
    }

    if (sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY) !== 'true') {
      event.preventDefault()
    }

    void recoverFromChunkLoadError()
  })
}

const Text = (props) => (
  <BaseText
    mb={4}
    lineHeight={1.5}
    fontSize={[2, 3]}
    color="grayapptitle"
    {...props}
  />
)

export const ErrorPage = ({
  title = 'Sorry, something went wrong',
  children,
}) => (
  <SideBySideLayout LinkComponent={ActiveLink}>
    <Flex flexDirection="column" css={{ textAlign: 'center' }}>
      <Heading mt={4} mb={4} color="grayapptitle" fontSize={[4, 5]}>
        {title}
      </Heading>
      {children ? (
        children
      ) : (
        <Text>
          Our team has been notified, we&apos;ll fix it as soon as we can!
        </Text>
      )}
      <Box>
        <PrimaryButton mt={4} as="a" href="/" uppercase>
          Go back and try again
        </PrimaryButton>
      </Box>
    </Flex>
  </SideBySideLayout>
)

export const RequestErrorReport = ({ eventId, user }) => (
  <ErrorPage>
    {eventId ? (
      <>
        <Text css={{ maxWidth: '80%' }} mx="auto">
          We’ve been notified of the error but you can help us track it down
          quicker by giving us a bit more information.
        </Text>
        <div>
          <PrimaryButton
            bg="white"
            color="graylabeltext"
            borderColor="graybuttonborder"
            onClick={() => showReportDialog({ eventId, user })}
            uppercase
          >
            Tell us what happened
          </PrimaryButton>
        </div>
      </>
    ) : null}
  </ErrorPage>
)

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { eventId: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  addUser = (scope) => {
    const { auth } = this.props
    if (auth && !auth.isEmpty) {
      scope.setUser({
        id: auth.uid,
        username: auth.displayName,
        email: auth.email,
      })
    }
  }

  getErrorReportUser = () => {
    const { auth } = this.props
    if (auth && !auth.isEmpty) {
      return {
        name: auth.displayName,
        email: auth.email,
      }
    }
  }

  componentDidCatch(error, errorInfo) {
    withScope((scope) => {
      addTags(scope)
      this.addUser(scope)
      scope.setExtras(errorInfo)
      const eventId = captureException(error)
      this.setState({ eventId })
    })

    handleChunkLoadError(error)
  }

  render() {
    if (this.state.hasError) {
      return (
        <RequestErrorReport
          eventId={this.state.eventId}
          user={this.getErrorReportUser()}
        />
      )
    }

    return this.props.children
  }
}

export default connect(({ firebase: { auth } }) => ({ auth }))(ErrorBoundary)
